import { Injectable, EventEmitter, Output } from '@angular/core';
import { Headers, Http, RequestOptions, RequestMethod, ResponseContentType } from '@angular/http';
import 'rxjs/add/operator/toPromise';
import 'rxjs/add/operator/finally';

import * as Models from '../models';
import { AngularAppConfig } from '../functions';

@Injectable()
export class BaseWebApiService {
    private hangingCalls: number = 0;
    @Output() isAsyncWaiting = new EventEmitter();
    @Output() isLongAsyncWaiting = new EventEmitter();
    @Output() isAsyncError = new EventEmitter();
    @Output() noAuthResponse = new EventEmitter();
    public URL_REST_PATH: string;

    constructor(private http: Http) {
        this.URL_REST_PATH = AngularAppConfig.RestServiceUrl();
    }

    getClientIP(): Promise<string> {
        return this.http.get('//api.ipify.org')
            .toPromise()
            .then(response => {
                return response.text() as string
            });
    }

    get(requestUrl: string, request: Models.WebApiRequestBase, registerToken: boolean = true, waitingStatus: boolean = true): Promise<Models.WebApiResponseBase> {
        if (waitingStatus) this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Get, request);
        return this.http.get(requestUrl, options)
            .toPromise()
            .then(response => this.handleResponse(response, registerToken, waitingStatus))
            .catch(error => this.handleError(error, waitingStatus));
    }

    post(requestUrl: string, request: Models.WebApiRequestBase, registerToken: boolean = true, waitingStatus: boolean = true): Promise<Models.WebApiResponseBase> {
        if (waitingStatus) this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Post, request);
        return this.http.post(requestUrl, JSON.stringify(request), options)
            .toPromise()
            .then(response => this.handleResponse(response, registerToken, waitingStatus))
            .catch(error => this.handleError(error, waitingStatus));
    }
    
    getRevive(requestUrl: string): Promise<string> {
        return this.http.get(requestUrl)
            .toPromise()
            .then(response => {
                return response.text() as string
            });
    }

    put(requestUrl: string, request: Models.WebApiRequestBase, registerToken: boolean = true, waitingStatus: boolean = true): Promise<Models.WebApiResponseBase> {
        if (waitingStatus) this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Put, request);
        return this.http.put(requestUrl, JSON.stringify(request), options)
            .toPromise()
            .then(response => this.handleResponse(response, registerToken, waitingStatus))
            .catch(error => this.handleError(error, waitingStatus));
    }

    delete(requestUrl: string, request: Models.WebApiRequestBase, registerToken: boolean = true, waitingStatus: boolean = true): Promise<Models.WebApiResponseBase> {
        if (waitingStatus) this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Delete, request);
        return this.http.delete(requestUrl, options)
            .toPromise()
            .then(response => this.handleResponse(response, registerToken, waitingStatus))
            .catch(error => this.handleError(error, waitingStatus));
    }

    private buildRequestOptions(destinationUrl: string, method: RequestMethod, request: Models.WebApiRequestBase): RequestOptions {
        request.token = AngularAppConfig.getUserToken();
        let options = new RequestOptions();
        options.headers = new Headers({ 'Content-Type': 'application/json' });
        options.method = method;
        options.body = JSON.stringify(request);
        options.url = destinationUrl;
        options.responseType = ResponseContentType.Json;
        options.withCredentials = false;
        return options;
    }

    private handleResponse(response: any, registerToken: boolean, waitingStatus: boolean): Models.WebApiResponseBase {
        let webApiResponseBase: Models.WebApiResponseBase = response.json() as Models.WebApiResponseBase;
        switch (webApiResponseBase.status) {
            case Models.ResponseStatusCodes.NOAUTH:
                if (registerToken) {
                    AngularAppConfig.setUserToken(null);
                    this.noAuthResponse.emit();
                }
                break;
            case Models.ResponseStatusCodes.KO:
                if (webApiResponseBase.token && webApiResponseBase.token != "" && registerToken) AngularAppConfig.setUserToken(webApiResponseBase.token);
                this.asyncErrorStatusChange(true, webApiResponseBase.message);
                break;
            case Models.ResponseStatusCodes.OK:
                if (registerToken) AngularAppConfig.setUserToken(webApiResponseBase.token);
                break;
        }
        if (waitingStatus) this.asyncWaitingStatusChange(false);
        return webApiResponseBase;
    }

    private handleError(error: any, waitingStatus): Promise<Models.WebApiResponseBase> {
        if (waitingStatus) this.asyncWaitingStatusChange(false);
        let response: Models.WebApiResponseBase = new Models.WebApiResponseBase();
        response.status = Models.ResponseStatusCodes.KO;
        if (!error.url || error.url == null) {
            response.message = "Errore Connessione Servizio REST";
        } else {
            response.message = error.message || error;
        }
        if (waitingStatus) this.asyncErrorStatusChange(true, response.message);
        return Promise.resolve(response);
    }

    asyncWaitingStatusChange(status: boolean): void {
        if (status) {
            this.hangingCalls++;
        } else {
            this.hangingCalls--;
        }

        if (this.hangingCalls > 0) {
            this.isAsyncWaiting.emit(this.hangingCalls > 0);
            var schedule = setTimeout(() => {
                this.isLongAsyncWaiting.emit(this.hangingCalls > 0);
                clearTimeout(schedule);
            }, 500);
        } else {
            this.isAsyncWaiting.emit(this.hangingCalls > 0);
            this.isLongAsyncWaiting.emit(this.hangingCalls > 0);
        }
    }

    asyncErrorStatusChange(status: boolean, messageError: string): void {
        let errorJson = {
            isError: status,
            message: messageError
        }
        this.isAsyncError.emit(errorJson);
    }

    downloadFile(requestUrl: string, request: Models.WebApiRequestBase): void {
        this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Post, request);
        options.responseType = ResponseContentType.ArrayBuffer;
        this.http.post(requestUrl, JSON.stringify(request), options)
            .map(response => response)
            .finally(() => { this.asyncWaitingStatusChange(false); })
            .subscribe(response => {
                var blob = new Blob([(<any>response)._body], { type: response.headers.get('Content-Type') });
                let download: string = response.headers.get('Content-Disposition').split('filename=')[1].split('"').join('');
                //window.alert("file caricato: "+download+ " dimensione: "+blob.size);
                if (window.navigator.msSaveOrOpenBlob) window.navigator.msSaveOrOpenBlob(blob, download);
                else {
                    var url = window.URL.createObjectURL(blob);
                    let a: any = document.createElement("a");
                    document.body.appendChild(a);
                    a.style = "display: none";
                    a.href = url;
                    a.download = download;
                    a.click();
                    window.URL.revokeObjectURL(url);
                }
            }
                , error => { this.asyncErrorStatusChange(true, error.statusText); }
            );
    }

    getFileContent(requestUrl: string, request: Models.WebApiRequest<any>): Promise<Blob> {
        this.asyncWaitingStatusChange(true);
        this.asyncErrorStatusChange(false, "");
        let options: RequestOptions = this.buildRequestOptions(requestUrl, RequestMethod.Post, request);
        options.responseType = ResponseContentType.ArrayBuffer;
        return this.http.post(requestUrl, JSON.stringify(request), options)
            .map(response => response)
            .finally(() => { this.asyncWaitingStatusChange(false); })
            .toPromise()
            .then(response => {
                var blob: Blob = new Blob([(<any>response)._body], { type: response.headers.get('Content-Type') });
                return blob;
            })
            .catch(error => {
                this.asyncErrorStatusChange(true, error.statusText);
                return Promise.resolve(null);
            });
    }
}


// WEBPACK FOOTER //
// ./src/angular_library/services/base-web-api-service.ts